import React from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Shield, TrendingUp, Code, Users } from "lucide-react";
import { motion } from "framer-motion";

export default function TeamSection({ texts }) {
    const areas = [
        {
            icon: Shield,
            emoji: "💡",
            title: texts.areas[0].title,
            description: texts.areas[0].description,
            background: texts.areas[0].background,
            gradient: "from-blue-500 to-cyan-500"
        },
        {
            icon: TrendingUp,
            emoji: "💻",
            title: texts.areas[1].title,
            description: texts.areas[1].description,
            background: texts.areas[1].background,
            gradient: "from-green-500 to-emerald-500"
        },
        {
            icon: Code,
            emoji: "🔬",
            title: texts.areas[2].title,
            description: texts.areas[2].description,
            background: texts.areas[2].background,
            gradient: "from-purple-500 to-violet-500"
        },
        {
            icon: Users,
            emoji: "👥",
            title: texts.areas[3].title,
            description: texts.areas[3].description,
            background: texts.areas[3].background,
            gradient: "from-orange-500 to-red-500"
        }
    ];

    return (
        <section id="team" className="py-24 relative z-10">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <Badge className="bg-gray-800 text-cyan-400 border-cyan-500/20 mb-4">
                        {texts.badge}
                    </Badge>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6">
                        <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                            {texts.title1}
                        </span>
                        <br />
                        <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                            {texts.title2}
                        </span>
                    </h2>
                    <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                        {texts.subtitle}
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-8 items-stretch">
                    {areas.map((area, index) => (
                        <motion.div
                            key={area.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: index * 0.15 }}
                            viewport={{ once: true }}
                        >
                            <Card className="bg-gray-900/50 border-gray-800 h-full hover:border-gray-700 transition-all duration-500 group rounded-2xl">
                                <CardContent className="px-10 pb-10 pt-10 h-full">
                                    <div className="flex items-start space-x-4 mb-8">
                                        <div className={`p-4 rounded-2xl bg-gradient-to-r ${area.gradient} bg-opacity-10 border border-gray-700 group-hover:bg-opacity-20 transition-all duration-300 mt-1`}>
                                            <span className="text-3xl">{area.emoji}</span>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors duration-300">
                                                {area.title}
                                            </h3>
                                            <p className="text-gray-400 leading-relaxed mb-8">
                                                {area.description}
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {area.background && area.background.split(' • ').map((bg) => (
                                                    <Badge key={bg} variant="secondary" className={`bg-gradient-to-r ${area.gradient} bg-opacity-10 border border-gray-700 text-gray-300 hover:text-white transition-colors duration-300`}>
                                                        {bg}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* Trust indicators */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    viewport={{ once: true }}
                    className="mt-16 text-center"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="space-y-2">
                            <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">5+</div>
                            <div className="text-sm text-gray-400">{texts.years}</div>
                        </div>
                        <div className="space-y-2">
                            <div className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">4</div>
                            <div className="text-sm text-gray-400">{texts.areasLabel}</div>
                        </div>
                        <div className="space-y-2">
                            <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">Elite</div>
                            <div className="text-sm text-gray-400">{texts.academic}</div>
                        </div>
                        <div className="space-y-2">
                            <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">Global</div>
                            <div className="text-sm text-gray-400">{texts.industry}</div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
